import { useState, useEffect, type KeyboardEvent } from 'react'
import { router, usePage } from '@inertiajs/react'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type ColumnDef,
  type RowData,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

declare module '@tanstack/react-table' {
  // Columns opt into server-side sorting by declaring the ransack sort key
  // (read by `q[s]`); columns without it are not sortable.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    sortKey?: string
  }
}
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface PagyMeta {
  count: number
  page: number
  items: number
  pages: number
  last: number
  vars: Record<string, unknown>
}

interface ServerDataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[]
  data: TData[]
  pagy: PagyMeta
  onRowClick?: (row: TData) => void
  basePath: string
  /** Id of the row whose detail sheet is currently open. Drives the active-row highlight. */
  selectedId?: string | number | null
  /** Extracts the stable id used to match `selectedId`. Defaults to `row.id`. */
  getRowId?: (row: TData) => string | number | null | undefined
  /** Message shown when there are no rows. */
  emptyMessage?: string
}

export function ServerDataTable<TData>({
  columns,
  data,
  pagy,
  onRowClick,
  basePath,
  selectedId,
  getRowId = (row) => (row as { id?: string | number }).id,
  emptyMessage = 'Aucun resultat.',
}: ServerDataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    rowCount: pagy.count,
    state: {
      pagination: {
        pageIndex: pagy.page - 1,
        pageSize: pagy.items,
      },
    },
    getCoreRowModel: getCoreRowModel(),
  })

  // All navigations (page, sort) are built from the live URL so each preserves
  // the others' params (filters, q[s], page, selected) regardless of how each
  // page shapes its props.
  const page = usePage()
  const query = page.url.includes('?') ? page.url.slice(page.url.indexOf('?') + 1) : ''
  const sortRaw = new URLSearchParams(query).get('q[s]')
  const [sortKey, sortDir] = sortRaw ? sortRaw.split(' ') : [null, null]

  function navigateWith(mutate: (params: URLSearchParams) => void) {
    const params = new URLSearchParams(query)
    mutate(params)
    const qs = params.toString()
    router.get(`${basePath}${qs ? `?${qs}` : ''}`, {}, { preserveState: true, preserveScroll: true })
  }

  function handlePageChange(newPageIndex: number) {
    navigateWith((p) => p.set('page', String(newPageIndex + 1)))
  }

  function handleSort(key: string) {
    const nextDir = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc'
    navigateWith((p) => {
      p.set('q[s]', `${key} ${nextDir}`)
      p.delete('page') // sorting returns to the first page
    })
  }

  const canPreviousPage = pagy.page > 1
  const canNextPage = pagy.page < pagy.pages

  // Table-scoped loading: Inertia router events are global, so only show
  // skeletons while a navigation to *this* table's basePath is in flight
  // (covers pagination, sort, filter, and in-sheet-mutation partial reloads).
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const offStart = router.on('start', (event) => {
      const visit = event.detail.visit
      // Partial visits (selection uses only:['pagy']) shouldn't flash the
      // skeleton — only full pagination/sort/filter navigations should. Full
      // visits carry an empty/absent `only`, so a non-empty `only` is the signal.
      if (Array.isArray(visit.only) && visit.only.length > 0) return
      if (visit.url.pathname.startsWith(basePath)) setLoading(true)
    })
    const offFinish = router.on('finish', () => setLoading(false))
    return () => {
      offStart()
      offFinish()
    }
  }, [basePath])

  const rows = table.getRowModel().rows

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const colSortKey = header.column.columnDef.meta?.sortKey
                  const isSorted = colSortKey != null && colSortKey === sortKey
                  const ariaSort: 'none' | 'ascending' | 'descending' | undefined =
                    colSortKey
                      ? isSorted
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  return (
                    <TableHead key={header.id} aria-sort={ariaSort}>
                      {header.isPlaceholder ? null : colSortKey ? (
                        <button
                          type="button"
                          onClick={() => handleSort(colSortKey)}
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {isSorted ? (
                            sortDir === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowDown className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length ? (
              rows.map((row, index) => {
                const rowId = getRowId(row.original)
                const isSelected =
                  selectedId != null &&
                  rowId != null &&
                  String(rowId) === String(selectedId)
                const isOdd = index % 2 === 1
                return (
                  <TableRow
                    key={row.id}
                    data-state={isSelected ? 'selected' : undefined}
                    // Clickable rows are keyboard-operable: focusable + Enter/Space
                    // open the sheet, announced as a button to screen readers.
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    className={cn(
                      onRowClick &&
                        'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      // Selected fill deterministically overrides the stripe
                      // (the selected branch excludes the odd-row class), so the
                      // active row stays distinct on striped rows too.
                      isSelected
                        ? 'bg-primary/10 hover:bg-primary/15'
                        : isOdd
                          ? 'bg-muted/30 hover:bg-muted/50'
                          : 'hover:bg-muted/50',
                    )}
                    onClick={() => onRowClick?.(row.original)}
                    onKeyDown={
                      onRowClick
                        ? (e: KeyboardEvent<HTMLTableRowElement>) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return
                            // Don't hijack activation of controls inside the row.
                            if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [contenteditable="true"]')) return
                            e.preventDefault()
                            onRowClick(row.original)
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <span>{emptyMessage}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-2">
        <p className="text-sm text-muted-foreground">
          {pagy.count} resultat{pagy.count !== 1 ? 's' : ''} — page {pagy.page} sur {pagy.pages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagy.page - 2)}
            disabled={!canPreviousPage}
          >
            Precedent
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagy.page)}
            disabled={!canNextPage}
          >
            Suivant
          </Button>
        </div>
      </div>
    </div>
  )
}
